import React, { useState, useEffect } from 'react';
import { Layout } from "./components/dashboard/layout";
import { Box, TextField, Typography, Button } from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ReportsMS = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportLogs, setReportLogs] = useState([]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [showPlainTable, setShowPlainTable] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const extractGradeNumber = (gradeStr) => {
    const match = gradeStr.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const [usersResponse, logsResponse] = await Promise.all([
        fetch("https://raptornet.aischennai.org/screening-manager/gate-api/getAllUsers.php"),
        fetch(`https://raptornet.aischennai.org/screening-manager/gate-api/getMsCheckInLogs.php?from_date=${formatDate(fromDate)}&to_date=${formatDate(toDate)}`)
      ]);

      const usersJson = await usersResponse.json();
      const logsJson = await logsResponse.json();

      const processedUsers = usersJson.users.map(user => ({
        userId: user.enroll_no,
        name: user.full_name,
        grade: extractGradeNumber(user.grade),
        role: user.user_type?.toLowerCase(),
        rfid: user.rfid_tag,
      }));

      const filteredStudents = processedUsers.filter(u => u.role === 'student' && u.grade > 5 && u.grade <= 8);
      setUsers(filteredStudents);
      setLogs(logsJson.data || []);

      processReport(filteredStudents, logsJson.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const url = `https://raptornet.aischennai.org/screening-manager/gate-api/getMsCheckInLogs.php?from_date=${formatDate(fromDate)}&to_date=${formatDate(toDate)}`;
      const response = await fetch(url);
      const result = await response.json();

      if (result.success) {
        setLogs(result.data);
        processReport(users, result.data);
      }
    } catch (err) {
      console.error("Log fetch failed:", err);
    }
    setIsLoading(false);
  };

  const [totalStudents, setTotalStudents] = useState(0);
  const [totalPresent, setTotalPresent] = useState(0);


  const processReport = (studentList, logData) => {
    const groupedLogs = new Map();
    const presentStudentKeys = new Set();

    const formattedFromDate = formatDate(fromDate);
    const formattedToDate = formatDate(toDate);

    logData.forEach(log => {
      const logDate = log.datetime.split(' ')[0];
      if (logDate >= formattedFromDate && logDate <= formattedToDate) {
        const student = studentList.find(u => String(u.userId) === String(log.id));
        if (student) {
          const key = `${student.userId}-${logDate}`;
          presentStudentKeys.add(key);
          if (!groupedLogs.has(key)) {
            groupedLogs.set(key, {
              userId: student.userId,
              name: student.name,
              grade: student.grade,
              date: logDate,
              logTime: log.datetime.split(' ')[1],
              mode: log.mode,
              present: true
            });
          }
        }
      }
    });

    // Add absentees
    const dateRange = [];
    const curr = new Date(fromDate);
    while (curr <= toDate) {
      dateRange.push(formatDate(new Date(curr)));
      curr.setDate(curr.getDate() + 1);
    }

    studentList.forEach(student => {
      dateRange.forEach(date => {
        const key = `${student.userId}-${date}`;
        if (!presentStudentKeys.has(key)) {
          groupedLogs.set(key, {
            userId: student.userId,
            name: student.name,
            grade: student.grade,
            date: date,
            logTime: '',
            mode: '',
            present: false
          });
        }
      });
    });

    setReportLogs([...groupedLogs.values()]);

    const hsStudents = reportLogs.filter(student => student.grade > 5 && student.grade <= 8);
    const presentToday = hsStudents.filter(student => student.present).length;

    setTotalStudents(hsStudents.length);
    setTotalPresent(presentToday);

  };

  return (
    <Layout>
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>MS Reports</Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" color="primary">
            Total MS Students: <strong>{totalStudents}</strong> | Present Today: <strong>{totalPresent}</strong>
          </Typography>
        </Box>


        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            dateFormat="yyyy-MM-dd"
            customInput={<TextField variant="outlined" label="From Date" />}
          />
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            dateFormat="yyyy-MM-dd"
            customInput={<TextField variant="outlined" label="To Date" />}
          />
          <Button variant="contained" color="secondary" onClick={fetchLogs} disabled={isLoading}>
            {isLoading ? 'Loading...' : "Search"}
          </Button>
        </Box>

          <Box sx={{ mt: 3 }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
              borderRadius: '8px', 
              overflow: 'hidden' 
            }}>
              <thead style={{ backgroundColor: '#f0f0f0' }}>
                <tr>
                  {['S No','User ID', 'Name', 'Grade', 'Mode', 'Status', 'Date', 'Log Time'].map((header, index) => (
                    <th key={index} style={{
                      padding: '12px',
                      textAlign: 'left',
                      borderBottom: '2px solid #ddd',
                      fontWeight: 600,
                      color: '#333'
                    }}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportLogs.map((log, index) => (
                  <tr
                    key={index}
                    style={{
                      backgroundColor: log.name.toLowerCase().includes("test")
                        ? "#ffe6e6" // light red highlight for "Test"
                        : index % 2 === 0
                        ? "#fff"
                        : "#fafafa",
                      borderBottom: "1px solid #eee"
                    }}
                  >

                    <td>{index+1}</td>
                    <td style={{ padding: '10px' }}>{log.userId}</td>
                    <td style={{ padding: '10px' }}>
                      
                      {log.name.toLowerCase().includes("test") ? (
                        <>
                        {log.name}&nbsp;&nbsp;
                        <span style={{
                          backgroundColor: "#007bff",
                          color: "white",
                          padding: "3px 8px",
                          borderRadius: "12px",
                          fontSize: "12px"
                        }}>
                         Test
                        </span>
                        </>
                      ) : (
                        log.name
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>{log.grade}</td>
                    <td style={{ padding: '10px' }}>{log.mode || '-'}</td>
                    <td style={{ 
                      padding: '10px', 
                      fontWeight: 600, 
                      color: log.present ? '#2e7d32' : '#c62828' 
                    }}>
                      {log.present ? 'Checked-In' : 'Not Checked-In'}
                    </td>
                    <td style={{ padding: '10px' }}>{log.date}</td>
                    <td style={{ padding: '10px' }}>{log.logTime || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
      </Box>
    </Layout>
  );
};

export default ReportsMS;